/**
 * Dynamic Script Loader
 * Loads external scripts once and returns a promise when ready.
 */

const GSI_SCRIPT_URL = 'https://accounts.google.com/gsi/client';
const APPLE_SCRIPT_URL = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
const SCRIPT_LOAD_TIMEOUT = 15000;

/** Singleton promises per script URL */
const loadPromises = new Map<string, Promise<void>>();

/**
 * Load a script by URL (singleton per URL)
 * @param src - Script URL
 * @returns Promise that resolves when script is loaded
 */
export function loadScript(src: string): Promise<void> {
  if (loadPromises.has(src)) {
    return loadPromises.get(src)!;
  }

  const promise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.defer = true;

    const timeoutId = setTimeout(() => {
      loadPromises.delete(src);
      reject(new Error(`Script load timeout: ${src}`));
    }, SCRIPT_LOAD_TIMEOUT);

    script.onload = () => {
      clearTimeout(timeoutId);
      resolve();
    };

    script.onerror = () => {
      clearTimeout(timeoutId);
      loadPromises.delete(src);
      reject(new Error(`Failed to load script: ${src}`));
    };

    document.head.appendChild(script);
  });

  loadPromises.set(src, promise);
  return promise;
}

export { GSI_SCRIPT_URL, APPLE_SCRIPT_URL };
